/**
 * Mobile App Router
 * 
 * This is the mobile version of the main App router.
 * It uses mobile-optimized components for all routes.
 */

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

// Mobile Components
import LandingMobile from './components/LandingMobile';
import AuthMobile from './components/AuthMobile';
import BrandCampaignManagerMobile from './components/BrandCampaignManagerMobile';
import CreatorCampaignUploadMobile from './components/CreatorCampaignUploadMobile';
import VideoResultMobile from './components/VideoResultMobile';
import SubscriptionPageMobile from './components/SubscriptionPageMobile';
import { MobileLayout } from './components/ui';

// Shared Components (these work on mobile too)
import EmailVerification from '@/components/email-verification';
import ResetPassword from '@/components/reset-password';
import TermsOfService from '@/components/terms-of-service';
import PrivacyPolicy from '@/components/privacy-policy';
import ApiPage from '@/components/api-page';
import Imprint from '@/components/imprint';
import SubscriptionSuccess from '@/components/subscription-success';
import SubscriptionCancel from '@/components/subscription-cancel';
import FreeCheckResult from '@/components/free-check-result';

// Protected Route wrapper for mobile
import ProtectedRoute from '@/components/protected-route';
import BrandProtectedRoute from '@/components/brand-protected-route';

// Import mobile CSS
import './mobile.css';

// Wrapper to give shared desktop pages a mobile header with back navigation
const MobilePageWrapper: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <MobileLayout title={title} showBack showMenu={false}>
    <div style={{ padding: 16 }}>
      {children}
    </div>
  </MobileLayout>
);

const MobileApp: React.FC = () => {
  return (
    <Routes>
      {/* Landing & Auth Routes */}
      <Route path="/" element={<LandingMobile />} />
      <Route path="/auth" element={<AuthMobile />} />
      <Route path="/verify" element={<MobilePageWrapper title="Verify Email"><EmailVerification /></MobilePageWrapper>} />
      <Route path="/reset-password" element={<MobilePageWrapper title="Reset Password"><ResetPassword /></MobilePageWrapper>} />
      
      {/* Legal & Info Pages (wrapped for mobile navigation) */}
      <Route path="/terms-of-service" element={<MobilePageWrapper title="Terms of Service"><TermsOfService embedded /></MobilePageWrapper>} />
      <Route path="/privacy-policy" element={<MobilePageWrapper title="Privacy Policy"><PrivacyPolicy embedded /></MobilePageWrapper>} />
      <Route path="/api" element={<MobilePageWrapper title="API"><ApiPage embedded /></MobilePageWrapper>} />
      <Route path="/imprint" element={<MobilePageWrapper title="Imprint"><Imprint embedded /></MobilePageWrapper>} />

      {/* Brand Portal Routes */}
      <Route
        path="/brand/campaign"
        element={
          <BrandProtectedRoute>
            <BrandCampaignManagerMobile />
          </BrandProtectedRoute>
        }
      />
      <Route
        path="/brand/campaigns"
        element={
          <BrandProtectedRoute>
            <BrandCampaignManagerMobile />
          </BrandProtectedRoute>
        }
      />

      {/* Subscription Routes */}
      <Route
        path="/subscription"
        element={
          <BrandProtectedRoute requireSubscription={false}>
            <SubscriptionPageMobile />
          </BrandProtectedRoute>
        }
      />
      <Route
        path="/subscription/success"
        element={
          <BrandProtectedRoute requireSubscription={false}>
            <MobilePageWrapper title="Subscription"><SubscriptionSuccess /></MobilePageWrapper>
          </BrandProtectedRoute>
        }
      />
      <Route
        path="/subscription/cancel"
        element={
          <BrandProtectedRoute requireSubscription={false}>
            <MobilePageWrapper title="Subscription"><SubscriptionCancel /></MobilePageWrapper>
          </BrandProtectedRoute>
        }
      />

      {/* Creator Routes */}
      <Route
        path="/upload"
        element={
          <ProtectedRoute>
            <CreatorCampaignUploadMobile />
          </ProtectedRoute>
        }
      />

      {/* Public Campaign Upload (no auth required) */}
      <Route 
        path="/upload/:campaign_uuid" 
        element={<CreatorCampaignUploadMobile />} 
      />

      {/* Result Routes */}
      <Route
        path="/result/:analysis_id"
        element={
          <ProtectedRoute>
            <VideoResultMobile />
          </ProtectedRoute>
        }
      />
      <Route 
        path="/campaign-result/:analysis_id" 
        element={<VideoResultMobile />} 
      />

      {/* Free video check results (public, no auth) */}
      <Route path="/free-check/:check_id" element={<FreeCheckResult />} />

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default MobileApp;
